/**
 * MessageBubble.jsx
 * Renders a single chat message — user (right) or bot (left).
 * Bot replies support basic markdown (bold, italic, lists).
 * Renders ScreeningQuiz when data.type === "screening_quiz".
 */

import React from "react";
import DataCard from "./DataCard";
import ScreeningQuiz from "./ScreeningQuiz";
import BulkScreeningQuiz from "./BulkScreeningQuiz";
import DisambiguationCard from "./DisambiguationCard";
import "../ChatPage.css";

function formatText(text) {
  // Bold: **text**
  // Italic: *text*
  // Line breaks
  const parts = text.split("\n");
  return parts.map((line, li) => {
    const segments = line.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
    return (
      <React.Fragment key={li}>
        {segments.map((seg, si) => {
          if (seg.startsWith("**") && seg.endsWith("**")) {
            return <strong key={si}>{seg.slice(2, -2)}</strong>;
          }
          if (seg.startsWith("*") && seg.endsWith("*")) {
            return <em key={si}>{seg.slice(1, -1)}</em>;
          }
          return seg;
        })}
        {li < parts.length - 1 && <br />}
      </React.Fragment>
    );
  });
}

export default function MessageBubble({ message, onScreeningSubmit, isLoading, onHasTable }) {
  const isUser = message.role === "user";
  const timestamp = message.timestamp
    ? new Date(message.timestamp).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

  const isScreeningQuiz =
    !isUser && message.data && message.data.type === "screening_quiz";
  const isBulkScreeningQuiz =
    !isUser && message.data && message.data.type === "bulk_screening_quiz";

  return (
    <div className={`hn-message-row ${isUser ? "hn-user-row" : "hn-bot-row"}`}>
      {!isUser && (
        <div className="hn-avatar hn-bot-avatar" title="HireNeuron AI">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 8V4H8" />
            <rect width="16" height="12" x="4" y="8" rx="2" />
            <path d="M2 14h2" />
            <path d="M20 14h2" />
            <path d="M15 13v2" />
            <path d="M9 13v2" />
          </svg>
        </div>
      )}

      <div className="hn-bubble-wrapper">
        <div className={`hn-bubble ${isUser ? "hn-user-bubble" : "hn-bot-bubble"}`}>
          <span className="hn-bubble-text">{formatText(message.content)}</span>
        </div>

        {/* Screening quiz card (single candidate) */}
        {isScreeningQuiz && (
          <ScreeningQuiz
            data={message.data}
            onSubmit={onScreeningSubmit}
            disabled={isLoading}
          />
        )}

        {/* Bulk screening quiz card (multiple candidates) */}
        {isBulkScreeningQuiz && (
          <BulkScreeningQuiz
            data={message.data}
            onSubmit={onScreeningSubmit}
            disabled={isLoading}
          />
        )}

        {/* Disambiguation card */}
        {!isUser && message.data && message.data.type?.endsWith("_disambiguation") && (
          <DisambiguationCard
            data={message.data}
            onSelect={onScreeningSubmit}
            disabled={isLoading}
          />
        )}

        {/* Regular structured data card */}
        {!isUser && message.data && !isScreeningQuiz && !isBulkScreeningQuiz && !message.data.type?.endsWith("_disambiguation") && (
          <DataCard data={message.data} apiName={message.action?.api} onHasTable={onHasTable} />
        )}

        {timestamp && (
          <span className={`hn-timestamp ${isUser ? "hn-ts-right" : "hn-ts-left"}`}>
            {timestamp}
          </span>
        )}
      </div>

      {isUser && (
        <div className="hn-avatar hn-user-avatar" title="You">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
        </div>
      )}
    </div>
  );
}
